// shared/src/tool-names.ts
//
// Single source of truth for the 27-tool LLM tool catalog (m-stage analyst
// prompts). Was previously hardcoded inline in m-contrarian-analyst.ts and
// referenced vaguely in m-consensus-analyst.ts ("e.g. fund flow data") — the
// latter caused the LLM to hallucinate non-existent tool names like
// `crypto_fund_flow` and `crypto_fund_flows` (check_log 2026-05-26 FAILs).
//
// This module exports:
//   · TOOL_NAMES_BY_TIER — { tier1: [...], ..., tier6: [...] } (the structural
//     grouping the prompts present to the LLM).
//   · TOOL_NAMES — flat readonly tuple of all 27 names, derived from the above.
//   · formatToolCatalogForPrompt() — multi-line "Tier N — <label>: name, name, …"
//     string used verbatim by both analyst prompts (drop-in replacement for the
//     hardcoded section).
//
// The catalog META (input_schema / output_schema / description) stays in
// workers/chain-workflow/src/tools/catalog.ts because it is dispatcher-side
// runtime data. catalog.ts re-imports TOOL_NAMES from here and exports
// VALID_NAMES (back-compat alias).

import { FRED_SERIES_LABELS } from './resolvers/fred-series-label.js';

export const TOOL_NAMES_BY_TIER = {
  tier1: ['fred_series', 'fred_macro_snapshot', 'macro_indicators_snapshot', 'fear_greed_history', 'econ_calendar', 'request_tickers'],
  tier2: ['crypto_global', 'crypto_defi_state', 'crypto_meta', 'crypto_ohlc', 'crypto_trending', 'vol_history'],
  tier3: ['equity_meta', 'analyst_view', 'earnings_calendar'],
  tier4: ['news_finnhub', 'news_rss', 'news_search'],
  tier5: ['tsa_volume', 'faa_delays', 'weather_alerts', 'weather_hub'],
  tier6: ['correlation', 'ratio_history', 'past_signals', 'signal_accuracy_recent', 'regime_history'],
} as const;

const TIER_LABELS = {
  tier1: 'Macro',
  tier2: 'Crypto',
  tier3: 'Equity',
  tier4: 'News',
  tier5: 'Alt-data',
  tier6: 'Compute',
} as const;

// Flat readonly tuple, order = tier order, intra-tier order = tier-array order.
export const TOOL_NAMES = [
  ...TOOL_NAMES_BY_TIER.tier1,
  ...TOOL_NAMES_BY_TIER.tier2,
  ...TOOL_NAMES_BY_TIER.tier3,
  ...TOOL_NAMES_BY_TIER.tier4,
  ...TOOL_NAMES_BY_TIER.tier5,
  ...TOOL_NAMES_BY_TIER.tier6,
] as const;

export type ToolName = typeof TOOL_NAMES[number];

/** Build the "## TOOL CATALOG" body used verbatim in both analyst prompts.
 *  Format:
 *    Tier 1 — Macro: <name>, <name>, …
 *    Tier 2 — Crypto: …
 *    …
 *  No leading/trailing blank lines; one tier per line. */
export function formatToolCatalogForPrompt(): string {
  const tiers: Array<keyof typeof TOOL_NAMES_BY_TIER> = ['tier1', 'tier2', 'tier3', 'tier4', 'tier5', 'tier6'];
  return tiers
    .map((tier, i) => `Tier ${i + 1} — ${TIER_LABELS[tier]}: ${TOOL_NAMES_BY_TIER[tier].join(', ')}`)
    .join('\n');
}

export function isValidToolName(name: string): name is ToolName {
  return (TOOL_NAMES as readonly string[]).includes(name);
}

/** The `fred_series` tool takes a free-form `series_id` the LLM supplies from
 *  memory — a nonexistent/discontinued/mistyped ID makes FRED answer HTTP 400
 *  "series does not exist" (check_log FAILs, briefs 2026-06-26 / 2026-07-07).
 *  Enumerate the curated, known-good IDs (the same set the macro grounding is
 *  labelled + validated against) so the model picks from these instead of
 *  guessing. Injected into both analyst prompts on its own line, adjacent to
 *  the formatToolCatalogForPrompt() catalog (kept separate so the catalog's
 *  each-tool-named-once test contract stays intact). Kept in lockstep with
 *  FRED_SERIES_LABELS via prompt-tool-names.test.ts. */
export function formatFredSeriesHint(): string {
  const ids = Object.entries(FRED_SERIES_LABELS)
    .map(([id, meta]) => `${id} (${meta.label})`)
    .join(', ');
  return `fred_series — valid series_id values (use ONLY these canonical FRED IDs; anything else 400s): ${ids}`;
}
